import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Button from "../Button";
import Modal from "../Modal";
import Spinner from "../Spinner";
import PlayerPreview from "./PlayerPreview";
import { addNotification } from "../Notification";
import { t } from "../../i18n/language";
import {
  EDITOR_BUILT_FOR,
  PLAYER_OPTION_FIELDS,
  changedOptions,
  effectiveOptions,
  isOlderThanEditor,
  listPlayersWithPlugin,
  listRecentVideos,
  savePlayerOptions,
} from "../utils/playerOptions";

/**
 * Set the Equal Play plugin's options on the account's players.
 *
 * These options have only ever been settable by pasting JSON into a box in
 * Brightcove Studio, which is fine for us and hostile to everyone else. The
 * person who actually wants to change how the sign language window looks is the
 * client's own manager, so this lives under /settings behind `is_admin` rather
 * than in /admin.
 *
 * Two things shape the design:
 *
 *   * Saving is live. Brightcove players have no draft state, so every save
 *     republishes and viewers see it on their next load. Hence the preview and
 *     the confirm step listing exactly what is about to change.
 *   * `signSize` and `signBgColor` are starting values, not guarantees — the
 *     plugin lets a viewer override both from its own settings panel, and the
 *     viewer's choice wins. The copy says so next to those two fields; without
 *     it the first bug report is "I set the colour and it didn't change".
 *
 * The write goes through the `setPluginOptions` Cloud Function, which checks
 * the caller against the server's copy of their user document and rewrites
 * nothing but the plugin's options.
 */

/* -- colour helpers ------------------------------------------------------- */

/** "rgba(40,40,40,0.7)" or "#282828" -> { hex, alpha }, for the two controls
 *  the browser gives us (a colour well has no alpha of its own). */
const splitColor = (value) => {
  const fallback = { hex: "#282828", alpha: 0.7 };
  if (typeof value !== "string") return fallback;

  const rgba = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/i.exec(
    value
  );
  if (rgba) {
    const hex = [rgba[1], rgba[2], rgba[3]]
      .map((part) => Number(part).toString(16).padStart(2, "0"))
      .join("");
    return {
      hex: `#${hex}`,
      alpha: rgba[4] === undefined ? 1 : Number(rgba[4]),
    };
  }

  const hex = /^#([0-9a-f]{6})$/i.exec(value.trim());
  if (hex) return { hex: `#${hex[1]}`, alpha: 1 };

  return fallback;
};

const joinColor = (hex, alpha) => {
  const parsed = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!parsed) return `rgba(40,40,40,${alpha})`;
  const [r, g, b] = parsed.slice(1).map((part) => parseInt(part, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

/* -- value formatting for the confirm step -------------------------------- */

const describeValue = (value) => {
  if (value === true) return t("on");
  if (value === false) return t("off");
  if (Array.isArray(value)) return value.join(", ");
  if (value === "") return t("default");
  return String(value);
};

/* -- the page ------------------------------------------------------------- */

function PlayerOptions() {
  const currentUser = useSelector((state) => state.user);
  const accountId = currentUser?.selectedAccount?.account_id;

  const [players, setPlayers] = useState();
  const [loadError, setLoadError] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [edited, setEdited] = useState({});

  const [videos, setVideos] = useState([]);
  const [previewVideoId, setPreviewVideoId] = useState("");

  const [confirm, setConfirm] = useState(null); // "one" | "all"
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");

  const selected = useMemo(
    () => (players || []).find((player) => player.id === selectedId),
    [players, selectedId]
  );

  const changed = useMemo(
    () => (selected ? changedOptions(selected.plugin, edited) : {}),
    [selected, edited]
  );

  const changedKeys = Object.keys(changed);

  // Everything on this page belongs to one Brightcove account, so drop all of
  // it the moment the account changes.
  //
  // Switching accounts in the navbar is only a Firestore write of
  // `selectedAccount` — no reload, no navigation — so a page that does not
  // clear its own state keeps showing the previous account's. Without this the
  // player list, the chosen player and the preview video all survived the
  // switch, and the preview went on playing a video from the account you had
  // just left. (Editor.js hides the same class of bug behind a
  // `window.location.reload()`; see TODO.md.)
  //
  // Declared before the loaders below so the clear lands in the same commit
  // that starts the new fetches, rather than after them.
  useEffect(() => {
    setPlayers(undefined);
    setSelectedId("");
    setEdited({});
    setVideos([]);
    setPreviewVideoId("");
    setLoadError(null);
  }, [accountId]);

  const loadPlayers = useCallback(() => {
    if (!accountId) return;

    setLoadError(null);
    listPlayersWithPlugin(accountId)
      .then((list) => {
        setPlayers(list);
        setSelectedId((current) =>
          list.some((player) => player.id === current)
            ? current
            : list[0]?.id || ""
        );
      })
      .catch((err) => {
        console.error("Could not list players", err);
        setPlayers([]);
        setLoadError(
          err?.message || t("Could not load the players for this account")
        );
      });
  }, [accountId]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  // Start each player's form from what that player actually has.
  useEffect(() => {
    if (!selected) return;
    setEdited(effectiveOptions(selected.plugin));
  }, [selected]);

  useEffect(() => {
    if (!accountId) return;

    listRecentVideos(accountId)
      .then((list) => {
        setVideos(list);
        // Default to something that will actually show an interpreter —
        // otherwise the sign options look like they do nothing.
        const withSign = list.find((video) => video.hasApprovedSign);
        // `current ||` keeps a video the operator picked themselves; the reset
        // above is what stops it carrying over from another account.
        setPreviewVideoId((current) => current || withSign?.id || "");
      })
      .catch((err) => {
        console.error("Could not list videos for the preview", err);
      });
  }, [accountId]);

  const setOption = (key, value) =>
    setEdited((current) => ({ ...current, [key]: value }));

  // Dragging or resizing the interpreter in the preview writes straight into
  // the position fields. Memoised because PlayerPreview binds it as a plugin
  // event listener - a new identity every render would re-bind it every render.
  const handleSignPosition = useCallback((position) => {
    setEdited((current) => ({ ...current, signPos: position }));
  }, []);

  const save = async (targets) => {
    setSaving(true);

    const failures = [];
    let done = 0;

    for (const player of targets) {
      setProgress(
        targets.length > 1
          ? `${t("Saving")} ${done + 1}/${targets.length}: ${player.name}`
          : ""
      );

      try {
        // Each player is sent only the options that differ from ITS own
        // configuration. Applying to all otherwise means stamping the selected
        // player's untouched values over every other player as well.
        const forThisPlayer =
          player.id === selectedId
            ? changed
            : changedOptions(player.plugin, { ...player.options, ...changed });

        if (Object.keys(forThisPlayer).length === 0) {
          done += 1;
          continue;
        }

        await savePlayerOptions({
          accountId,
          playerId: player.id,
          options: forThisPlayer,
        });
        done += 1;
      } catch (err) {
        console.error(`Could not save options for player ${player.id}`, err);
        failures.push(`${player.name}: ${err.message}`);
      }
    }

    setSaving(false);
    setProgress("");
    setConfirm(null);

    if (failures.length === 0) {
      addNotification(
        t("Options saved"),
        targets.length > 1
          ? `${targets.length} ${t("players updated and published.")}`
          : t("The player has been updated and published.")
      );
    } else {
      addNotification(
        t("Some players were not updated"),
        failures.join(" — "),
        "bg-red-600"
      );
    }

    // Re-read from Brightcove rather than patching local state, so what the
    // form shows is what the players actually have.
    loadPlayers();
  };

  if (!currentUser?.uid) {
    return <Spinner />;
  }

  if (!currentUser.is_admin) {
    return (
      <p className="text-sm text-gray-700">
        {t("Only an administrator can change player settings.")}
      </p>
    );
  }

  const confirmTargets =
    confirm === "all" ? players || [] : selected ? [selected] : [];

  return (
    <>
      <h2 className="text-xl font-semibold">{t("Player settings")}</h2>
      <p className="mt-1 max-w-2xl text-sm text-gray-700">
        {t(
          "Change how Equal Play looks on your players. Saving publishes straight away — viewers see the change the next time they load a video."
        )}
      </p>

      {!players && !loadError && (
        <div className="mt-4">
          <Spinner />
        </div>
      )}

      {loadError && (
        <p className="mt-4 text-sm text-gray-700 bg-yellow-100 border border-yellow-300 rounded px-3 py-2">
          {loadError}
        </p>
      )}

      {players && players.length === 0 && !loadError && (
        <p className="mt-4 text-sm text-gray-700">
          {t("None of your players have Equal Play installed yet.")}
        </p>
      )}

      {/* Side by side as soon as there is room — an even split at md, and from
          lg the preview takes the wider share, since it is a 16:9 player while
          the form is a column of narrow controls. */}
      {players && players.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* -- the form -- */}
          <div className="lg:col-span-2">
            <label className="block text-sm" htmlFor="playerOptionsPlayer">
              {t("Player")}
            </label>
            <select
              id="playerOptionsPlayer"
              className="mt-1 w-full h-10 pl-2 rounded border border-gray-200 bg-white"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {players.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.name} ({player.id})
                </option>
              ))}
            </select>

            {selected && isOlderThanEditor(selected.version) && (
              <p className="mt-2 text-sm text-gray-700 bg-yellow-100 border border-yellow-300 rounded px-3 py-2">
                {t("This player has Equal Play")} {selected.version}.{" "}
                {t(
                  "Settings added after that version are saved but ignored until the player is updated."
                )}{" "}
                {t("The newest settings need")} {EDITOR_BUILT_FOR}.
              </p>
            )}

            <fieldset className="mt-5">
              <legend className="text-sm font-semibold">
                {t("Player controls")}
              </legend>
              {PLAYER_OPTION_FIELDS.filter(
                (field) => field.group === "controls"
              ).map((field) => (
                <OptionField
                  key={field.key}
                  field={field}
                  value={edited[field.key]}
                  disabled={
                    field.dependsOn ? !edited[field.dependsOn] : false
                  }
                  onChange={(value) => setOption(field.key, value)}
                />
              ))}
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-sm font-semibold">
                {t("Sign language window")}
              </legend>
              {PLAYER_OPTION_FIELDS.filter(
                (field) => field.group === "sign"
              ).map((field) => (
                <OptionField
                  key={field.key}
                  field={field}
                  value={edited[field.key]}
                  onChange={(value) => setOption(field.key, value)}
                  onReset={
                    field.key === "signPos"
                      ? () =>
                          setOption(
                            "signPos",
                            effectiveOptions(selected.plugin).signPos
                          )
                      : undefined
                  }
                />
              ))}
            </fieldset>

          </div>

          {/* -- the preview --
              Sticky, and first in the stacked layout. The form is a long
              column and the Settings sidebar takes a third of the page, so
              without this the player ends up below the fold and you cannot see
              what you are changing — which is the whole point of it. */}
          {/* No z-index: the navbar's account dropdown is also z-10, and this
              column comes later in the document, so raising it here puts the
              player on top of the dropdown and makes the account unswitchable.
              Being sticky already positions it above the form beside it. */}
          <div className="lg:col-span-3 order-first md:order-none self-start sticky top-0 bg-gray-100 pt-2 pb-3">
            <label className="block text-sm" htmlFor="playerOptionsVideo">
              {t("Preview with")}
            </label>
            <select
              id="playerOptionsVideo"
              className="mt-1 w-full h-10 pl-2 rounded border border-gray-200 bg-white"
              value={previewVideoId}
              onChange={(e) => setPreviewVideoId(e.target.value)}
            >
              <option value="">{t("— Select a video —")}</option>
              {videos.map((video) => (
                <option key={video.id} value={video.id}>
                  {video.hasApprovedSign ? "◉ " : ""}
                  {video.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-600">
              {t("◉ marks videos with sign language you can see here.")}
            </p>

            <div className="mt-3">
              {selected && (
                <PlayerPreview
                  accountId={accountId}
                  playerId={selected.id}
                  videoId={previewVideoId}
                  options={edited}
                  onSignPosition={handleSignPosition}
                />
              )}
            </div>

            {/* Saving lives with the preview rather than at the foot of the
                form: this column is the sticky one, so the buttons and the
                unsaved-change count stay on screen however far down the
                controls you have scrolled. */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                text={t("Save")}
                onClick={() => setConfirm("one")}
                disabled={changedKeys.length === 0 || saving}
              />
              <Button
                text={t("Save to all players on this account")}
                colour="white"
                onClick={() => setConfirm("all")}
                disabled={
                  changedKeys.length === 0 || saving || players.length < 2
                }
              />
              <Button
                text={t("Undo changes")}
                colour="white"
                onClick={() => setEdited(effectiveOptions(selected.plugin))}
                disabled={changedKeys.length === 0 || saving}
              />
              {changedKeys.length > 0 && (
                <span className="text-xs text-gray-600">
                  {changedKeys.length} {t("unsaved change(s)")}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        width="w-96"
        show={!!confirm}
        title={
          confirm === "all"
            ? t("Save to all players on this account")
            : t("Save player settings")
        }
        subtitle={
          confirm === "all"
            ? `${confirmTargets.length} ${t("players")}`
            : selected?.name
        }
        acceptButtonText={t("Save and publish")}
        acceptDisabled={saving}
        working={saving}
        onClose={() => !saving && setConfirm(null)}
        onAccept={() => save(confirmTargets)}
      >
        <p className="text-sm">
          {t(
            "These settings go live immediately. There is no draft and no undo."
          )}
        </p>

        <h4 className="mt-3 text-sm font-semibold">{t("About to change")}</h4>
        <ul className="mt-1 text-sm">
          {changedKeys.map((key) => {
            const field = PLAYER_OPTION_FIELDS.find((f) => f.key === key);
            const before = selected
              ? effectiveOptions(selected.plugin)[key]
              : undefined;
            return (
              <li key={key} className="py-1 border-b border-gray-100">
                {field ? field.label() : key}:{" "}
                <span className="text-gray-500 line-through">
                  {describeValue(before)}
                </span>{" "}
                → <strong>{describeValue(changed[key])}</strong>
              </li>
            );
          })}
        </ul>

        {confirm === "all" && (
          <>
            <h4 className="mt-3 text-sm font-semibold">{t("Players")}</h4>
            <ul className="mt-1 text-sm text-gray-700">
              {confirmTargets.map((player) => (
                <li key={player.id}>{player.name}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-gray-600">
              {t(
                "Each player keeps its other settings — only the changes listed above are applied."
              )}
            </p>
          </>
        )}

        {progress && <p className="mt-3 text-sm">{progress}</p>}
      </Modal>
    </>
  );
}

/** One control, chosen by the field's `kind`. */
function OptionField({ field, value, disabled = false, onChange, onReset }) {
  const id = `playerOption-${field.key}`;

  if (field.kind === "boolean") {
    return (
      <div className="mt-3">
        <label
          className={`flex items-start text-sm ${
            disabled ? "text-gray-400" : ""
          }`}
          htmlFor={id}
        >
          <input
            id={id}
            type="checkbox"
            className="mt-1 mr-2"
            checked={!!value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>
            {field.label()}
            {field.help && (
              <span className="block text-xs text-gray-600">
                {field.help()}
              </span>
            )}
          </span>
        </label>
      </div>
    );
  }

  if (field.kind === "color") {
    const { hex, alpha } = splitColor(value);
    return (
      <div className="mt-3">
        <label className="block text-sm" htmlFor={id}>
          {field.label()}
        </label>
        <div className="mt-1 flex items-center gap-3">
          <input
            id={id}
            type="color"
            className="h-9 w-14 rounded border border-gray-200 bg-white"
            value={hex}
            onChange={(e) => onChange(joinColor(e.target.value, alpha))}
          />
          <div className="flex-1">
            <label className="block text-xs text-gray-600" htmlFor={`${id}-a`}>
              {t("Opacity")} {Math.round(alpha * 100)}%
            </label>
            <input
              id={`${id}-a`}
              type="range"
              className="w-full"
              min="0"
              max="1"
              step="0.05"
              value={alpha}
              onChange={(e) => onChange(joinColor(hex, Number(e.target.value)))}
            />
          </div>
        </div>
        {field.help && (
          <p className="mt-1 text-xs text-gray-600">{field.help()}</p>
        )}
      </div>
    );
  }

  if (field.kind === "size") {
    // The plugin stores 20-50 and shows the viewer a 1-100 expansion of it.
    // The number on screen here is the stored one; a percentage of the player
    // width is what it actually means.
    return (
      <div className="mt-3">
        <label className="block text-sm" htmlFor={id}>
          {field.label()}: {value}%
        </label>
        <input
          id={id}
          type="range"
          className="mt-1 w-full"
          min="20"
          max="50"
          step="1"
          value={value ?? 35}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        {field.help && (
          <p className="mt-1 text-xs text-gray-600">{field.help()}</p>
        )}
      </div>
    );
  }

  if (field.kind === "position") {
    const position = Array.isArray(value) ? value : [70, 35, 25];
    const parts = [
      { index: 0, label: t("From left") },
      { index: 1, label: t("From top") },
      { index: 2, label: t("Width") },
    ];
    return (
      <div className="mt-3">
        <div className="flex items-baseline justify-between">
          <span className="block text-sm">{field.label()}</span>
          {onReset && (
            <button
              type="button"
              className="text-xs text-equalplay-dark underline hover:no-underline"
              onClick={onReset}
            >
              {t("Reset position")}
            </button>
          )}
        </div>
        <div className="mt-1 flex gap-2">
          {parts.map((part) => (
            <div key={part.index} className="flex-1">
              <label
                className="block text-xs text-gray-600"
                htmlFor={`${id}-${part.index}`}
              >
                {part.label} %
              </label>
              <input
                id={`${id}-${part.index}`}
                type="number"
                className="mt-1 w-full px-2 py-2 border border-gray-200 rounded focus:outline-none focus:shadow-outline"
                min="0"
                max="100"
                value={position[part.index]}
                onChange={(e) => {
                  const next = [...position];
                  // Clamped here as well as in the Cloud Function: a number
                  // input accepts anything typed, min/max only style it.
                  next[part.index] = Math.max(
                    0,
                    Math.min(100, Number(e.target.value) || 0)
                  );
                  onChange(next);
                }}
              />
            </div>
          ))}
        </div>
        {field.help && (
          <p className="mt-1 text-xs text-gray-600">{field.help()}</p>
        )}
      </div>
    );
  }

  // "label": a string, or false for "use the plugin's own translated label".
  return (
    <div className="mt-3">
      <label className="block text-sm" htmlFor={id}>
        {field.label()}
      </label>
      <input
        id={id}
        type="text"
        className="mt-1 w-full px-2 py-2 border border-gray-200 rounded focus:outline-none focus:shadow-outline"
        value={value === false || value === undefined ? "" : value}
        onChange={(e) => onChange(e.target.value === "" ? false : e.target.value)}
      />
      {field.help && (
        <p className="mt-1 text-xs text-gray-600">{field.help()}</p>
      )}
    </div>
  );
}

export default PlayerOptions;
